/**
 * Base d'aliments personnelle — volet de l'onglet Bibliothèque (specs §6.4, §8.2).
 *
 * ⚠️ ARBITRAGE Bibliothèque / Réglages, déjà tranché à l'étape 5 et appliqué
 * ici : les **objets** du référentiel vivent à la Bibliothèque (§8.2), les
 * **préférences de comportement** aux Réglages (§6.8). Un aliment est un objet,
 * au même titre qu'un modèle de journée ou qu'une recette : il se crée, se
 * corrige et se supprime ici.
 *
 * ⚠️ **Supprimer un aliment ne touche pas au journal** (§4.1) : les entrées déjà
 * enregistrées gardent leur libellé et leurs macros, figés à la saisie. L'écran
 * le dit, parce que c'est exactement l'inverse de ce qu'on redoute en cliquant
 * sur « Supprimer ».
 *
 * 🔴 **La recherche est LOCALE depuis la V2, étape 4** (§3.1). L'écran charge la
 * base personnelle **en entier** sous une clé stable (`['foods', '']`,
 * persistée) et filtre en mémoire — il n'interroge plus le serveur à chaque
 * frappe.
 *
 * Ce n'était pas une optimisation : avec le terme dans la clé de cache, un mot
 * jamais tapé auparavant n'avait rien à restaurer hors ligne, la requête restait
 * en pause (`networkMode: 'online'`, voulu pour les lectures) et l'écran
 * attendait indéfiniment. Le §3.1 promet pourtant « rechercher dans la base
 * personnelle » sans réseau.
 *
 * ⚠️ Toujours **insensible aux accents** — « cafe » trouve « Café ». Le
 * classement vient de `shared/search.ts`, le **même** module que la route du
 * serveur : le résultat ne change pas d'ordre selon qu'on a du réseau ou non.
 * Le débounce disparaît avec l'aller-retour qu'il servait à espacer.
 *
 * ⚠️ **Refonte** : champ de recherche en pilule avec sa loupe
 * (`.search-field`), liste en `.list-card` — étoile de favori à gauche, marque
 * en second plan derrière le nom, pilule « Suppr. » à droite. Le débounce, les
 * requêtes et les mutations sont inchangés.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { FoodDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { createFood, deleteFood, fetchFoods, updateFood, QUICK_ACCESS_KEY } from '../../api/foods';
import { Button, Card, GlyphSearch, GlyphStar, TextField } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { searchFoodsLocally } from '../../lib/localSearch';
import { formatMacro } from '../../lib/macros';
import { FoodEditorSheet } from './FoodEditor';
import {
  draftOfFood,
  emptyFoodDraft,
  inputOfDraft,
  updateOfDraft,
  type FoodDraft,
} from './foodDraft';

type Editing = { mode: 'create' } | { mode: 'edit'; food: FoodDTO };

function FoodRow({
  food,
  onEdit,
  onToggleFavorite,
  onDelete,
  busy,
}: {
  food: FoodDTO;
  onEdit: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <li className="n-list__row">
      {/*
        ⚠️ L'étoile dit son état de **trois** façons : le tracé (plein /
        contour), la teinte, et l'`aria-label` du bouton. La couleur ne le porte
        jamais seule — c'est ce qui l'autorise à être là.
      */}
      <Button
        variant="ghost"
        shape="circle"
        className={food.isFavorite ? 'n-star' : 'n-star n-star--off'}
        aria-pressed={food.isFavorite}
        aria-label={
          food.isFavorite ? `Retirer ${food.name} des favoris` : `Mettre ${food.name} en favori`
        }
        onClick={onToggleFavorite}
      >
        <GlyphStar filled={food.isFavorite} />
      </Button>

      <button type="button" onClick={onEdit} className="n-list__main">
        <span className="n-list__name">
          {food.name}
          {food.brand !== null && <span className="n-list__brand">{food.brand}</span>}
        </span>
        <span className="n-list__meta tnum">
          100 {food.baseUnit} : {formatMacro(food.per100.protein, 'g')} P ·{' '}
          {formatMacro(food.per100.carbs, 'g')} G · {formatMacro(food.per100.fat, 'g')} L ·{' '}
          {formatMacro(food.per100.kcal, 'kcal')} kcal
          {food.portions.length > 0 && ` · ${String(food.portions.length)} portion(s)`}
          {/* La provenance se lit, elle ne se devine pas à une couleur. */}
          {food.source === 'openfoodfacts' && ' · importé d’OFF'}
        </span>
      </button>

      <Button
        variant="danger"
        disabled={busy}
        onClick={onDelete}
        aria-label={`Supprimer ${food.name}`}
      >
        Suppr.
      </Button>
    </li>
  );
}

export function FoodsSection() {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [draft, setDraft] = useState<FoodDraft>(emptyFoodDraft);

  /*
   * 🔴 **La réplique du §3.1 — une seule clé, sans le terme dedans.** C'est la
   * même entrée de cache que celle qu'emploie l'éditeur de recettes pour son
   * sélecteur d'ingrédients, et que le scanner interroge hors ligne : une base,
   * un cache, une vérité.
   */
  const foods = useQuery({ queryKey: ['foods', ''], queryFn: () => fetchFoods('') });

  const cherche = term.trim();
  const resultats = useMemo(
    () => searchFoodsLocally(foods.data ?? [], cherche),
    [foods.data, cherche],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['foods'] });
    void queryClient.invalidateQueries({ queryKey: QUICK_ACCESS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['food-search'] });
  };

  const save = useMutation({
    mutationFn: () =>
      editing?.mode === 'edit'
        ? updateFood(editing.food.id, updateOfDraft(draft))
        : createFood(inputOfDraft(draft)),
    onSuccess: () => {
      refresh();
      setEditing(null);
    },
  });

  const favorite = useMutation({
    mutationFn: (food: FoodDTO) => updateFood(food.id, { isFavorite: !food.isFavorite }),
    onSuccess: refresh,
  });

  const remove = useMutation({ mutationFn: deleteFood, onSuccess: refresh });

  return (
    <div className="n-page">
      {/*
        ⚠️ La loupe est `lead` : décorative, masquée aux lecteurs d'écran. C'est
        `aria-label` qui nomme le champ — une icône décrit, elle ne nomme pas.
      */}
      <TextField
        shape="pill"
        lead={<GlyphSearch />}
        aria-label="Chercher dans ma base d’aliments"
        placeholder="Chercher dans ma base…"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        /*
          ⚠️ Pas de sélection au focus ici, contrairement aux champs de valeur :
          on **complète** une recherche, on ne la corrige pas. C'est le défaut
          inverse de celui des champs pré-remplis.
        */
        selectOnFocus={false}
      />

      {foods.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(foods)} quoi="la base d’aliments" />
      {save.isError && <ErrorNote>{save.error.message}</ErrorNote>}
      {remove.isError && <ErrorNote>{remove.error.message}</ErrorNote>}
      {favorite.isError && <ErrorNote>{favorite.error.message}</ErrorNote>}

      {foods.data !== undefined && resultats.length === 0 && (
        <p className="n-empty">
          {cherche === ''
            ? 'Aucun aliment. Les fiches se créent ici, ou se remplissent toutes seules en scannant un code-barres depuis le journal.'
            : `Aucun aliment ne correspond à « ${cherche} ».`}
        </p>
      )}

      {resultats.length > 0 && (
        <Card padding="none">
          <ul className="n-list">
            {resultats.map((food) => (
              <FoodRow
                key={food.id}
                food={food}
                busy={remove.isPending}
                onEdit={() => {
                  setDraft(draftOfFood(food));
                  setEditing({ mode: 'edit', food });
                }}
                onToggleFavorite={() => {
                  favorite.mutate(food);
                }}
                onDelete={() => {
                  remove.mutate(food.id);
                }}
              />
            ))}
          </ul>
        </Card>
      )}

      <Button
        variant="primary"
        size="lg"
        block
        onClick={() => {
          setDraft(emptyFoodDraft());
          setEditing({ mode: 'create' });
        }}
      >
        Nouvel aliment
      </Button>

      <p className="n-foot-note">
        Supprimer un aliment ne touche pas au journal : les entrées déjà enregistrées gardent leur
        libellé et leurs macros, figés à la saisie.
      </p>

      {editing && (
        <FoodEditorSheet
          // Une clé par cible d'édition : le brouillon repart de zéro quand on
          // passe d'une fiche à une autre.
          key={editing.mode === 'edit' ? `edit-${String(editing.food.id)}` : 'create'}
          open
          title={editing.mode === 'edit' ? `Modifier « ${editing.food.name} »` : 'Nouvel aliment'}
          draft={draft}
          onDraftChange={setDraft}
          saving={save.isPending}
          existing={editing.mode === 'edit' ? editing.food : undefined}
          onSubmit={() => {
            save.mutate();
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
